import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { getUserProfileByUid } from '../../services/userProfileService';
import { signOutUser, signInWithEmailPassword, signUpWithEmailPassword } from '../../services/authService';
import { sendWebReadyOnce } from '../../bridge/webviewBridge';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

const MIN_SPLASH_MS = 2800;

const SplashPage = () => {
  const navigate = useNavigate();
  const { currentUser, loading, refreshUser, userData } = useAuth();
  const resolving = useRef(false);
  const splashStart = useRef(Date.now());
  const [ready, setReady] = useState(false);
  const role = userData?.role;

  const roleResolved = !!role;
  const bgColor = !roleResolved ? '#FFFFFF'
    : role === 'student' ? '#22C55E'
    : role === 'parent' ? '#AB47BC'
    : role === 'admin' ? '#1E293B'
    : '#4A7CFE';

  useEffect(() => {
    sendWebReadyOnce();
    // 소셜 로그인 결과는 AuthContext에서 처리
    const timer = setTimeout(() => setReady(true), MIN_SPLASH_MS);
    return () => clearTimeout(timer);
  }, []);

  const pendingNav = useRef(null);

  const safeNavigate = (path, opts) => {
    if (ready) {
      navigate(path, opts);
    } else {
      pendingNav.current = { path, opts };
    }
  };

  useEffect(() => {
    if (ready && pendingNav.current) {
      const { path, opts } = pendingNav.current;
      pendingNav.current = null;
      navigate(path, opts);
    }
  }, [ready, navigate]);

  useEffect(() => {
    if (loading || resolving.current) return;

    resolving.current = true;

    (async () => {
      if (!currentUser) {
        let autoLoginAttempted = false;
        try {
          const saved = JSON.parse(localStorage.getItem('classcare.autoLogin'));
          if (saved?.loginId && saved?.password) {
            const q2 = query(collection(db, 'users'), where('loginId', '==', saved.loginId));
            const snap = await getDocs(q2);
            if (!snap.empty) {
              const ud = snap.docs[0].data();
              if (ud.password === saved.password && (!ud.status || ud.status === 'approved')) {
                const fakeEmail = `${saved.loginId}@classmanage.local`;
                try {
                  await signInWithEmailPassword({ email: fakeEmail, password: saved.password, skipEnsure: true });
                } catch {
                  await signUpWithEmailPassword({ email: fakeEmail, password: saved.password });
                }
                autoLoginAttempted = true;
              }
            }
            if (!autoLoginAttempted) localStorage.removeItem('classcare.autoLogin');
          }
        } catch {
          localStorage.removeItem('classcare.autoLogin');
        }
        resolving.current = false;
        if (!autoLoginAttempted) safeNavigate('/login', { replace: true });
        return;
      }

      try {
        const profile = await getUserProfileByUid(currentUser.uid);
        await refreshUser();

        if (!profile) {
          safeNavigate('/link-phone', { replace: true });
          return;
        }

        const isAdminCreated = !!profile.loginId;

        if (!profile.phoneE164 && !isAdminCreated) {
          safeNavigate('/link-phone', { replace: true });
          return;
        }

        if (!profile.name || !profile.role) {
          safeNavigate('/complete-profile', { replace: true });
          return;
        }

        if (profile.status !== 'approved' && !isAdminCreated) {
          safeNavigate('/pending', { replace: true });
          return;
        }

        safeNavigate('/home', { replace: true });
      } catch (err) {
        console.error('SplashPage 분기 실패:', err);
        safeNavigate('/login', { replace: true });
      } finally {
        resolving.current = false;
      }
    })();
  }, [loading, currentUser, navigate, refreshUser]);

  return (
    <Container $bg={bgColor} $resolved={roleResolved}>
      <TitleWrap>
        <Title $resolved={roleResolved}>ClassCare</Title>
        <Sparkle $resolved={roleResolved}>✦</Sparkle>
      </TitleWrap>
      <SubText $resolved={roleResolved}>스마트 학원 관리</SubText>
      <Spinner $resolved={roleResolved} />
    </Container>
  );
};

export default SplashPage;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  gap: 12px;
  transition: background 0.5s ease;
`;

const TitleWrap = styled.div`
  position: relative;
  display: inline-block;
`;

/* 0s ~ 0.8s: 타이틀 페이드 + 살짝 위로 */
const Title = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 44px;
  font-weight: 800;
  color: ${({ $resolved }) => $resolved ? 'white' : '#4A7CFE'};
  letter-spacing: 1px;
  opacity: 0;
  transform: translateY(8px);
  animation: titleIn 0.8s ease forwards;
  transition: color 0.4s;
  @keyframes titleIn {
    to { opacity: 1; transform: translateY(0); }
  }
`;

/* 1.0s ~ 2.5s: 글씨 완전히 나온 뒤 흰색 별 반짝임 */
const Sparkle = styled.span`
  position: absolute;
  top: -4px;
  right: -16px;
  font-size: 18px;
  color: ${({ $resolved }) => $resolved ? 'white' : '#4A7CFE'};
  opacity: 0;
  filter: drop-shadow(0 0 6px ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.8)' : 'rgba(74,124,254,0.5)'});
  animation: sparkle 2s ease-in-out 1.0s forwards;
  transition: color 0.4s, filter 0.4s;
  @keyframes sparkle {
    0% { opacity: 0; transform: scale(0.2) rotate(-30deg); }
    15% { opacity: 1; transform: scale(1.4) rotate(10deg); }
    35% { opacity: 0.9; transform: scale(1) rotate(5deg); }
    55% { opacity: 1; transform: scale(1.2) rotate(8deg); }
    75% { opacity: 0.7; transform: scale(0.9) rotate(0deg); }
    100% { opacity: 0; transform: scale(0.3) rotate(-10deg); }
  }
`;

/* 0.5s ~ 1.1s: 서브텍스트 */
const SubText = styled.p`
  font-size: 14px;
  color: ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.75)' : '#9CA3AF'};
  letter-spacing: 2px;
  opacity: 0;
  animation: fadeIn 0.6s ease 0.5s forwards;
  transition: color 0.4s;
  @keyframes fadeIn {
    to { opacity: 1; }
  }
`;

/* 1.2s: 스피너 */
const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.3)' : '#E5E7EB'};
  border-top-color: ${({ $resolved }) => $resolved ? 'white' : '#4A7CFE'};
  border-radius: 50%;
  margin-top: 28px;
  opacity: 0;
  animation: fadeIn 0.4s ease 1.2s forwards, spin 0.8s linear 1.2s infinite;
  transition: border-color 0.4s;
  @keyframes spin { to { transform: rotate(360deg); } }
`;
